import { TEHRAN_TIME_ZONE, fromJalali, gregorianToJalali, jalaliDayRange, jalaliToGregorian, tehranDayDelta, toJalali, } from '@bookora/shared';
import dayjs from 'dayjs';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
function pad2(value) {
    return String(value).padStart(2, '0');
}
function asDate(value) {
    if (dayjs.isDayjs(value))
        return value.toDate();
    if (value instanceof Date)
        return value;
    if (typeof value === 'string' && DATE_ONLY.test(value.trim())) {
        // A bare API date is a Tehran day, not the UTC midnight `new Date()` makes
        // of it — which in Tehran is still the same day, but only by 3½ hours.
        return parseDateOnly(value) ?? new Date(Number.NaN);
    }
    return new Date(value);
}
/** `'YYYY-MM-DD'` → Tehran noon of that Gregorian date, or `null`. */
function parseDateOnly(value) {
    const match = DATE_ONLY.exec(value.trim());
    if (!match)
        return null;
    const gy = Number(match[1]);
    const gm = Number(match[2]);
    const gd = Number(match[3]);
    if (gm < 1 || gm > 12 || gd < 1 || gd > 31)
        return null;
    try {
        const { jy, jm, jd } = gregorianToJalali(gy, gm, gd);
        const noon = fromJalali(jy, jm, jd, 12, 0, 0, 0, TEHRAN_TIME_ZONE);
        // An impossible date (`2026-02-31`) rolls forward inside the day-number
        // arithmetic; refuse it rather than silently moving the day.
        return toApiDate(noon) === `${String(gy)}-${pad2(gm)}-${pad2(gd)}` ? noon : null;
    }
    catch {
        return null;
    }
}
/**
 * The Gregorian date (`YYYY-MM-DD`, ASCII) of the Tehran calendar day `value`
 * falls on. What every date-only API field wants.
 */
export function toApiDate(value) {
    const { jy, jm, jd } = toJalali(asDate(value), TEHRAN_TIME_ZONE);
    const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);
    return `${String(gy)}-${pad2(gm)}-${pad2(gd)}`;
}
/** Today's Tehran date as the API spells it. */
export function todayApiDate(now = new Date()) {
    return toApiDate(now);
}
/**
 * A picker value for an API date.
 *
 * `'YYYY-MM-DD'` becomes NOON of that Tehran day: half a day from either
 * midnight, so neither the picker nor a stray local-zone call can land it on
 * a neighbouring date. A full ISO instant is kept as the instant it is.
 * `null` for an empty or unparseable input, so a form's initial value can be
 * `fromApiDate(row.date)` without a guard.
 */
export function fromApiDate(value) {
    if (!value)
        return null;
    if (DATE_ONLY.test(value.trim())) {
        // Never handed on to dayjs, which reads `2026-02-31` as 3 March.
        const dateOnly = parseDateOnly(value);
        return dateOnly ? dayjs(dateOnly) : null;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
}
/** Tehran midnight at the start of `value`'s day. */
export function startOfTehranDay(value = new Date()) {
    return dayjs(jalaliDayRange(asDate(value), TEHRAN_TIME_ZONE).start);
}
/**
 * The last millisecond of `value`'s Tehran day — the inclusive end a picker
 * shows. An API that takes a half-open range wants
 * `startOfTehranDay(value.add(1, 'day'))` instead; see `toApiRange`.
 */
export function endOfTehranDay(value = new Date()) {
    return dayjs(jalaliDayRange(asDate(value), TEHRAN_TIME_ZONE).end.getTime() - 1);
}
/** Tehran midnight today. */
export function tehranToday(now = new Date()) {
    return startOfTehranDay(now);
}
/** Whole Tehran calendar days from `b` to `a` (`a − b`). */
export function tehranDaysBetween(a, b) {
    return tehranDayDelta(asDate(a), asDate(b), TEHRAN_TIME_ZONE);
}
/** `value` is on an earlier Tehran day than `now` — «no past dates». */
export function isBeforeTehranToday(value, now = new Date()) {
    return tehranDaysBetween(value, now) < 0;
}
/** `value` is on a later Tehran day than `now` — «no future dates». */
export function isAfterTehranToday(value, now = new Date()) {
    return tehranDaysBetween(value, now) > 0;
}
/** `value` is on the same Tehran day as `now`. */
export function isTehranToday(value, now = new Date()) {
    return tehranDaysBetween(value, now) === 0;
}
//# sourceMappingURL=tehran-day.js.map